/**
 * The tenancy's feature-flag read, shared by `<DashboardFeatureFlags>` (which
 * keeps it fresh and feeds the flag store) and the few surfaces that have to
 * explain a flag rather than just read it. See `DashboardFeatureFlags.tsx` for
 * how a flag is declared, set and read.
 */
import * as React from "react";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { useConfigSnapshot } from "./configSnapshot";

/** One entry per tenancy: a switch reads afresh instead of reusing the last answer. */
export function featureFlagsQueryKey(identity: StorageIdentity) {
  return [
    "feature-flags",
    identity.customerId,
    identity.orgId,
    identity.tenantId,
    identity.userId,
  ] as const;
}

/**
 * The one read. Every observer shares its cache entry; only the `<DashboardFeatureFlags>`
 * observer keeps it fresh — a `passive` one (a page asking whether the read has
 * settled) must not start a second timer for the same GET.
 */
export function useTenancyFeatureFlags({ passive = false }: { passive?: boolean } = {}) {
  const snapshot = useConfigSnapshot(passive);
  return { ...snapshot, data: snapshot.data?.features };
}

/**
 * Whether the tenancy's flags have been read (or failed to be, or there is no
 * tenancy to read them for) — so the declared defaults are the ANSWER rather
 * than a placeholder.
 *
 * For the rare surface that has to say why a feature is missing. Saying "not
 * switched on for you" in the moment before the read lands and switches it on
 * is a false statement, however briefly. Everything else reads a flag with
 * `useFeatureFlag` and simply shows the default meanwhile.
 */
export function useFeatureFlagsSettled(): boolean {
  const { isPending, fetchStatus } = useTenancyFeatureFlags({ passive: true });
  return !isPending || fetchStatus === "idle";
}

/**
 * Which RUNG decided each flag for this tenancy — `customer` / `org` / `tenant`
 * / `user`, or `default` when nobody set it.
 *
 * The store cannot answer this: `featureFlagLayer` fills every declared flag
 * into the claim layer, defaults included, so the layer reads "claim" for a flag
 * an operator set and for one nobody has touched alike. The backend already
 * distinguishes them (it is what the admin portal's Effective column shows), so
 * a surface that has to explain WHY a flag is on reads it from the response.
 */
export function useFeatureFlagSources(): Record<string, string> {
  const { data } = useTenancyFeatureFlags({ passive: true });
  return React.useMemo(() => {
    const out: Record<string, string> = {};
    for (const [name, flag] of Object.entries(data?.flags ?? {})) out[name] = flag.source;
    return out;
  }, [data]);
}
